/*
 * Service worker registration helper for Edotask.
 *
 * Why a custom helper instead of CRA's default registerSW?
 *   - CRA 5 ships its SW disabled by default; we want explicit control.
 *   - We only register in production builds and in secure contexts to avoid
 *     dev-server caching headaches.
 *
 * The helper exposes a tiny pub-sub for online/offline state so any component
 * (e.g. the OfflineBanner) can react without prop drilling.
 */

const listeners = new Set();
let lastOnlineState = typeof navigator !== 'undefined' ? navigator.onLine : true;

const notify = () => {
  const state = navigator.onLine;
  if (state === lastOnlineState) return;
  lastOnlineState = state;
  listeners.forEach((cb) => {
    try { cb(state); } catch (err) { console.error('Offline listener threw:', err); }
  });
};

if (typeof window !== 'undefined') {
  window.addEventListener('online', notify);
  window.addEventListener('offline', notify);
}

export const subscribeOnlineStatus = (callback) => {
  listeners.add(callback);
  // Fire once so the subscriber gets the current value.
  try { callback(navigator.onLine); } catch { /* noop */ }
  return () => listeners.delete(callback);
};

export const isOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine);

export const registerServiceWorker = async () => {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) {
    console.info('[SW] Service workers not supported in this browser');
    return;
  }
  // Skip on http:// to avoid silent failures.
  if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
    console.info('[SW] Skipping registration on non-secure origin');
    return;
  }
  // We register in both production AND preview HTTPS. In CRA dev mode this
  // disables hot-module-reload, but offline support is the bigger win for
  // Edotask — the user can always tap "Reset app data" in Settings to wipe
  // the SW + caches if they want HMR back.
  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js');
    console.info('[SW] Registered with scope:', registration.scope);
    // Auto-reload when a new SW takes control. This is the cleanest way to
    // ensure that users on a stale build pick up bug fixes (like the offline
    // patches) the next time they open the app, without needing to manually
    // clear caches or use the Reset App Data button.
    let reloading = false;
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloading) return;
        reloading = true;
        console.info('[SW] New worker took control — reloading');
        window.location.reload();
      });
    }
    registration.addEventListener('updatefound', () => {
      const installing = registration.installing;
      if (!installing) return;
      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed' && navigator.serviceWorker.controller) {
          console.info('[SW] Update installed — activating');
        }
      });
    });
    // Proactively check for updates every 5 minutes so long-running sessions
    // don't sit on stale code.
    setInterval(() => {
      registration.update().catch(() => {});
    }, 5 * 60 * 1000);
  } catch (err) {
    console.warn('[SW] Registration failed:', err);
  }
};

export const clearServiceWorkerCache = async () => {
  if (typeof navigator === 'undefined' || !navigator.serviceWorker?.controller) return;
  navigator.serviceWorker.controller.postMessage({ type: 'EDOTASK_CLEAR_CACHE' });
};
